import { BorderTable } from '@/components/BorderTable';
import { DraggableTable } from '@/components/DraggableTable';
import { TopRowOverProvider } from '@/components/DraggableTable/context/TopRowOverProvider';
import {
  ACTION_TYPE,
  DragHandle,
  TActionType,
} from '@/components/DraggableTable/DragHandle/DragHandle';
import { Row, RowProps } from '@/components/DraggableTable/Row/Row';
import { useTransactionItemsColumns } from '@/features/transaction_items/List/Table/Column/column';
import { TransactionItemsColumn } from '@/features/transaction_items/List/type';
import { Active, Over } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ModernPaginationProps,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import { ColumnType } from 'antd/es/table';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

export type Props = {
  table: TransactionItemsTableProps;
  dataSource: TransactionItemsColumn[];
  setDataSource: Dispatch<SetStateAction<TransactionItemsColumn[]>>;
};

export type TransactionItemsTableProps = Omit<
  TableProps<TransactionItemsColumn>,
  'columns' | 'emptyType' | 'pagination'
>;

export const ReorderableTable: FC<Props> = memo(
  ({ table, dataSource, setDataSource }) => {
    const [selectedRowId, setSelectedRowId] = useState<string | number | null>(
      null
    );
    const [shownMenuId, setShownMenuId] = useState<string | number | null>(
      null
    );
    const tableRef = useRef<HTMLDivElement>(null);

    const onSetDataSource = useCallback(
      (active: Active, over: Over | null, isShowTop: boolean) => {
        setDataSource((prevState) => {
          const activeIndex = prevState.findIndex(
            (record) => record.id === active?.id
          );
          let overIndex = prevState.findIndex(
            (record) => record.id === over?.id
          );
          const selectedId = prevState[activeIndex]?.id;
          if (!over) {
            setSelectedRowId(selectedId ?? null);
            requestAnimationFrame(() => {
              const tableBody =
                tableRef.current?.querySelector('.rci-table-body');
              const row = tableBody?.querySelector(
                `tr[data-row-key="${selectedId}"]`
              );
              row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            return prevState;
          }

          if (overIndex !== 0 && overIndex === activeIndex - 1) {
            return prevState;
          }

          if (
            (overIndex !== 0 && activeIndex > overIndex) ||
            (overIndex === 0 && !isShowTop)
          ) {
            overIndex = overIndex + 1;
          }

          setSelectedRowId(selectedId ?? null);
          const newDataSource = arrayMove(
            prevState,
            activeIndex,
            overIndex
          ).map((record, index) => ({
            ...record,
            priority: index + 1,
          }));
          return newDataSource;
        });
      },
      [setDataSource]
    );

    const onSetShownMenuId = useCallback((id: string | number | null) => {
      setShownMenuId(id);
      setSelectedRowId(null);
    }, []);

    const onClickAction = useCallback(
      (action: TActionType, id: string) => {
        setDataSource((prevState) => {
          const index = prevState.findIndex((record) => record.id === id);
          let newIndex = index;

          switch (action) {
            case ACTION_TYPE.UP:
              newIndex = Math.max(index - 1, 0);
              break;
            case ACTION_TYPE.DOWN:
              newIndex = Math.min(index + 1, prevState.length - 1);
              break;
            case ACTION_TYPE.TOP:
              newIndex = 0;
              break;
            case ACTION_TYPE.BOTTOM:
              newIndex = prevState.length - 1;
              break;
          }

          if (newIndex !== index) {
            const newDataSource = arrayMove(prevState, index, newIndex).map(
              (record, index) => ({
                ...record,
                priority: index + 1,
              })
            );
            return newDataSource;
          }
          return prevState;
        });
        setShownMenuId(null);
        if (
          tableRef.current &&
          (action === ACTION_TYPE.TOP || action === ACTION_TYPE.BOTTOM)
        ) {
          const tableBody = tableRef.current.querySelector('.rci-table-body');
          tableBody?.scrollTo({
            top: action === ACTION_TYPE.TOP ? 0 : tableBody.scrollHeight,
            behavior: 'smooth',
          });
        }
      },
      [setDataSource, tableRef]
    );

    const baseColumns = useTransactionItemsColumns({
      isReorderingMode: true,
    });

    const columns: ColumnType<TransactionItemsColumn>[] = useMemo(() => {
      const dragHandleColumn: ColumnType<TransactionItemsColumn> = {
        width: 40,
        title: ' ',
        render: (_: unknown, record: TransactionItemsColumn, index: number) => (
          <DragHandle
            setShownMenuId={onSetShownMenuId}
            id={record.id}
            shownMenuId={shownMenuId}
            onClickAction={onClickAction}
            index={index}
            lastIndex={dataSource.length - 1}
          />
        ),
      };

      return [dragHandleColumn, ...baseColumns];
    }, [baseColumns, dataSource, onSetShownMenuId, shownMenuId, onClickAction]);

    const onDragStart = useCallback(() => {
      setSelectedRowId(null);
      setShownMenuId(null);
    }, []);

    const componentsConfig = useMemo(
      () => ({
        body: {
          row: (props: RowProps) => {
            return (
              <Row
                {...props}
                selectedRowId={selectedRowId}
                shownMenuId={shownMenuId}
                setSelectedRowId={setSelectedRowId}
                setShownMenuId={setShownMenuId}
              />
            );
          },
        },
      }),
      [selectedRowId, shownMenuId, setSelectedRowId, setShownMenuId]
    );

    const pagination: ModernPaginationProps = useMemo(() => {
      return {
        currentPage: 1,
        maxPage: 1,
        showPerPage: true,
      };
    }, []);

    return (
      <div ref={tableRef}>
        <TopRowOverProvider>
          <DraggableTable
            data={dataSource}
            dataSource={dataSource}
            droppedRowId={selectedRowId}
            setDroppedRowId={setSelectedRowId}
            setDataSource={onSetDataSource}
            onDragStart={onDragStart}
          >
            <BorderTable
              {...table}
              columns={columns}
              data={dataSource}
              scroll={table.scroll}
              rowKey='id'
              components={componentsConfig}
              pagination={pagination}
              legacy={false}
            />
          </DraggableTable>
        </TopRowOverProvider>
      </div>
    );
  }
);

ReorderableTable.displayName = 'ReorderableTable';
